/*
 * Thunderstorm is a full web app framework!
 *
 * Typescript & Express backend infrastructure that natively runs on firebase function
 * Typescript & React frontend infrastructure
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { BeLogged, exists, ImplementationMissingException, LogClient_BrowserGroups, merge, ModuleManager, Promise_all_sequentially, removeItemFromArray } from '@nu-art/ts-common';
import { ThunderDispatcher } from './thunder-dispatcher.js';
import '../styles/impl/basic.scss';
import '../styles/impl/icons.scss';
import * as RDC from 'react-dom/client';
import { appWithJSX } from './AppWrapper.js';
import { StorageKey } from '../modules/ModuleFE_LocalStorage.js';
import axios from 'axios';
import { getBrowseAndDeviceLogs, getECMADataLog, getiFrameLog, getJSEngineLogs, globalErrorListener, indexedDBAsyncCheckLog, navigationTimingLog } from './thunder-helpers.js';
export const Storage_AppVersion = new StorageKey('app-version').withstandDeletion();
export class Thunder extends ModuleManager {
    listeners = [];
    renderFunc;
    props;
    preBuildActions = [];
    innerConfig;
    constructor(config) {
        super();
        BeLogged.addClient(LogClient_BrowserGroups);
        // @ts-ignore
        window.thunder = this;
        // @ts-ignore
        ThunderDispatcher['listenersResolver'] = () => this.listeners;
        this.addPreBuildAction(this.fetchConfig.bind(this)); // add config resolver as a pre build action
        this.innerConfig = Object.freeze(config);
    }
    static getInstance() {
        return Thunder.instance;
    }
    init() {
        super.init();
        const appJsx = this.renderFunc?.(this.props);
        if (!appJsx)
            throw new ImplementationMissingException('Could not get app from Thunder!');
        //Set root div and its attributes
        const rootDiv = document.createElement('div');
        rootDiv.classList.add('match_parent');
        rootDiv.setAttribute('id', 'root');
        document.body.appendChild(rootDiv);
        //Set app root
        RDC.createRoot(rootDiv).render(appJsx);
        return this;
    }
    async fetchConfig() {
        try {
            this.config = {};
            const promises = [];
            if (this.innerConfig.defaultConfigUrl)
                promises.push(axios.get(this.innerConfig.defaultConfigUrl));
            else
                promises.push((async () => ({ data: {} }))());
            promises.push(axios.get(this.innerConfig.configUrl));
            const [defaultConfig, envConfig] = await Promise.all(promises);
            if (!exists(defaultConfig?.data))
                return this.logWarning('Could not resolve default config');
            if (!exists(envConfig?.data))
                return this.logWarning('Could not resolve env config');
            if (typeof defaultConfig?.data !== 'object')
                return this.logWarning('default config is not an object');
            if (typeof envConfig?.data !== 'object')
                return this.logWarning('env config is not an object');
            this.config = merge(merge(this.config, defaultConfig.data), envConfig.data);
        }
        catch (err) {
            this.logError('failed loading config with error', err);
        }
    }
    addUIListener(listener) {
        this.logVerbose(`Register UI listener: ${listener}`);
        this.listeners.push(listener);
    }
    removeUIListener(listener) {
        this.logVerbose(`Unregister UI listener: ${listener}`);
        removeItemFromArray(this.listeners, listener);
    }
    /**
     * Set up the entry point for the app.
     * @param mainApp - The entry point into the application, what will be rendered directly under root (usually App.tsx)
     * @param props - The props to go with the supplied main app
     * @param renderFunc - The app wrapper, which will usually provide a router. by default is "appWithJSX" which will just return the app
     */
    setMainApp(mainApp, props = {}, renderFunc = appWithJSX) {
        this.props = {
            element: mainApp,
            props: props
        };
        this.renderFunc = renderFunc;
        return this;
    }
    build(onStarted) {
        Promise_all_sequentially(this.preBuildActions).then(() => {
            super.build();
            onStarted?.();
        });
    }
    setVersion(version) {
        Storage_AppVersion.set(version);
        return super.setVersion(version);
    }
    getConfig() {
        return this.config;
    }
    addPreBuildAction = (func) => {
        this.preBuildActions.push(func);
        return this;
    };
    logStuff = () => {
        getBrowseAndDeviceLogs();
        getJSEngineLogs();
        getECMADataLog();
        getiFrameLog();
        navigationTimingLog();
        globalErrorListener();
        indexedDBAsyncCheckLog();
    };
    getEnvironment() {
        return Thunder.getInstance().getConfig().label;
    }
}
//# sourceMappingURL=Thunder.js.map